import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Camera, X, CaretLeft, CaretRight } from "@phosphor-icons/react";
import ArtDecoDivider from "../components/ArtDecoDivider";

const HERO_BG =
    "https://images.unsplash.com/photo-1470337458703-46ad1756a187?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

// Add photo URLs here as { src, aspect } — aspect controls the grid rhythm.
const PHOTOS = [
    { src: "/images/events/img_4450.webp", aspect: "aspect-[3/4]" },
    { src: "/images/events/img_3925.webp", aspect: "aspect-square" },
    { src: "/images/events/img_2403.webp", aspect: "aspect-[3/4]" },
    { src: "/images/events/img_2794.webp", aspect: "aspect-square" },
    { src: "/images/events/img_2793.webp", aspect: "aspect-[3/4]" },
    { src: "/images/events/img_2103.webp", aspect: "aspect-square" },
    { src: "/images/events/img_3785.webp", aspect: "aspect-[3/4]" },
    { src: "/images/events/img_2109.webp", aspect: "aspect-square" },
    { src: "/images/events/od_photo.webp", aspect: "aspect-[3/4]" },
];

const PLACEHOLDER_SLOTS = [
    { aspect: "aspect-[3/4]" },
    { aspect: "aspect-square" },
    { aspect: "aspect-[3/4]" },
    { aspect: "aspect-square" },
    { aspect: "aspect-[3/4]" },
    { aspect: "aspect-square" },
];

function PhotoCard({ src, aspect, idx, onOpen }) {
    return (
        <button
            type="button"
            onClick={() => onOpen(idx)}
            data-testid={`event-photo-${idx + 1}`}
            className={`group relative ${aspect} overflow-hidden border border-white/10 bg-obsidian-soft cursor-pointer text-left w-full`}
            aria-label={`View photo ${idx + 1} full screen`}
        >
            <img
                src={src}
                alt="Recent event"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </button>
    );
}

function Lightbox({ photos, index, onClose, onPrev, onNext }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") onPrev();
            if (e.key === "ArrowRight") onNext();
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose, onPrev, onNext]);

    return (
        <div
            className="fixed inset-0 z-[100] bg-obsidian/95 backdrop-blur-md flex items-center justify-center animate-fade-in"
            data-testid="lightbox-overlay"
            onClick={onClose}
        >
            <button
                type="button"
                onClick={onClose}
                data-testid="lightbox-close"
                aria-label="Close"
                className="absolute top-5 right-5 z-10 w-11 h-11 border border-white/20 text-white/80 hover:text-gold hover:border-gold/60 transition-colors duration-200 flex items-center justify-center"
            >
                <X size={22} weight="thin" />
            </button>

            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onPrev(); }}
                data-testid="lightbox-prev"
                aria-label="Previous photo"
                className="absolute left-3 md:left-8 z-10 w-11 h-11 md:w-12 md:h-12 border border-white/20 text-white/80 hover:text-gold hover:border-gold/60 transition-colors duration-200 flex items-center justify-center"
            >
                <CaretLeft size={24} weight="thin" />
            </button>

            <img
                src={photos[index].src}
                alt={`Recent event ${index + 1}`}
                data-testid="lightbox-image"
                onClick={(e) => e.stopPropagation()}
                className="max-h-[85vh] max-w-[85vw] object-contain border border-white/10 shadow-2xl"
            />

            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onNext(); }}
                data-testid="lightbox-next"
                aria-label="Next photo"
                className="absolute right-3 md:right-8 z-10 w-11 h-11 md:w-12 md:h-12 border border-white/20 text-white/80 hover:text-gold hover:border-gold/60 transition-colors duration-200 flex items-center justify-center"
            >
                <CaretRight size={24} weight="thin" />
            </button>

            <div
                className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-xs tracking-overline uppercase"
                data-testid="lightbox-counter"
            >
                {index + 1} <span className="text-gold/60 mx-1">/</span> {photos.length}
            </div>
        </div>
    );
}

function EmptySlot({ aspect, idx }) {
    return (
        <div
            data-testid={`event-photo-slot-${idx + 1}`}
            className={`relative ${aspect} bg-obsidian-soft border border-white/10 overflow-hidden flex items-center justify-center`}
        >
            <div className="absolute inset-0 grain-bg opacity-40" aria-hidden="true" />
            <div className="relative text-center px-6">
                <Camera size={36} weight="thin" className="text-gold/50 mx-auto mb-3" />
                <div className="text-white/40 text-xs tracking-overline uppercase">
                    Photo Coming Soon
                </div>
            </div>
        </div>
    );
}

export default function RecentEvents() {
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const closeLightbox = useCallback(() => setLightboxIndex(null), []);
    const prevPhoto = useCallback(
        () => setLightboxIndex((i) => (i - 1 + PHOTOS.length) % PHOTOS.length),
        []
    );
    const nextPhoto = useCallback(
        () => setLightboxIndex((i) => (i + 1) % PHOTOS.length),
        []
    );

    return (
        <div className="bg-obsidian min-h-screen" data-testid="page-recent-events">
            {/* HERO */}
            <section className="relative h-[60vh] min-h-[440px] flex items-end overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={HERO_BG}
                        alt=""
                        className="w-full h-full object-cover opacity-40"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-obsidian/30" />
                </div>
                <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12 w-full pb-16">
                    <div className="text-gold text-xs tracking-overline uppercase mb-4 flex items-center gap-3">
                        <span className="w-10 h-px bg-gold" />
                        The Gallery
                    </div>
                    <h1
                        className="font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-3xl"
                        data-testid="events-hero-title"
                    >
                        Recent <em className="gold-italic">Events</em>
                    </h1>
                    <p className="text-white/70 text-lg md:text-xl font-light max-w-2xl mt-6">
                        A look at the evenings we've poured — weddings, private
                        parties, and everything in between.
                    </p>
                </div>
            </section>

            {/* GALLERY */}
            <section className="py-20 px-6 lg:px-12">
                <div className="max-w-6xl mx-auto">
                    <div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                        data-testid="events-gallery-grid"
                    >
                        {PHOTOS.length > 0
                            ? PHOTOS.map((p, idx) => (
                                  <PhotoCard
                                      key={idx}
                                      src={p.src}
                                      aspect={p.aspect}
                                      idx={idx}
                                      onOpen={setLightboxIndex}
                                  />
                              ))
                            : PLACEHOLDER_SLOTS.map((s, idx) => (
                                  <EmptySlot key={idx} aspect={s.aspect} idx={idx} />
                              ))}
                    </div>
                </div>
            </section>

            <div className="max-w-6xl mx-auto px-6 lg:px-12">
                <ArtDecoDivider />
            </div>

            {/* CTA */}
            <section className="py-24 px-6 lg:px-12 text-center">
                <div className="text-gold text-xs tracking-overline uppercase mb-4">
                    Your Event Could Be Next
                </div>
                <h2 className="font-display text-white text-4xl md:text-5xl mb-8">
                    Let's make it a <em className="gold-italic">night</em>
                </h2>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/book" className="btn-gold" data-testid="events-cta-book">
                        Reserve The Evening
                    </Link>
                    <Link to="/services" className="btn-gold-outline" data-testid="events-cta-services">
                        View Services
                    </Link>
                </div>
            </section>

            {lightboxIndex !== null && (
                <Lightbox
                    photos={PHOTOS}
                    index={lightboxIndex}
                    onClose={closeLightbox}
                    onPrev={prevPhoto}
                    onNext={nextPhoto}
                />
            )}
        </div>
    );
}
